import React, { Fragment, useCallback, useState } from "react";
import useForm from "../../../../hooks/useForm";
import Input from "../../../form/Input";
import Button from "../../../form/Button";
import { authLogin, authSignup } from "../../../../services/authService";
import useMutation from "../../../../hooks/useMutation";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { login } from "../../../../redux/slice/authSlice";
import { useDispatch } from "react-redux";

const signUpValidation = {
  email: [
    {
      type: "email",
      required: true,
      message: "Enter valid Email",
    },
  ],
  password: [
    {
      type: "password",
      minLength: 8,
      required: true,
      message:
        "Password must be strong (min 8 characters, including uppercase, lowercase, number, and symbol)",
    },
  ],
  confirmPassword: { required: true, message: "Confirm Password is Required" },
  firstName: { required: true, message: "Please Enter the First Name" },
  lastName: { required: true, message: "Please Enter the Last Name" },
};

const signInValidation = {
  loginEmail: [
    {
      type: "email",
      required: true,
      message: "Enter valid Email",
    },
  ],
  // loginPassword: { required: true, message: "Password is Required" },
};

const AuthenticationForm = ({ currentView, donorData }) => {
  const { session, loginPending, loginData } = useSelector(
    (state) => state?.authProvider
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginAs, setLoginAs] = useState(
    location.pathname.includes("donor") ? "donor" : "user"
  );

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules:
      currentView === "signup" ? signUpValidation : signInValidation,
  });

  const {
    mutate: signup,
    error: signUpError,
    isPending: signUpPending,
    data: signUpData,
  } = useMutation({
    mutationFn: async (val) => {
      return await authSignup({
        ...val,
        requestType: "SIGNUP",
      });
    },
    onSuccess: async (res) => {
      if (res?.status === 200 || res?.status === 201) {
        navigate("/account-verification");
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
  });

  const handleFormSubmit = useCallback(
    (event) => {
      handleSubmit(
        event,
        currentView === "signin"
          ? (val) => {
              dispatch(
                login({
                  email: val?.loginEmail,
                  password: val?.loginPassword,
                  loginType: loginAs,
                  redirect: "/user/dashboard",
                  navigate,
                  location,
                })
              );
            }
          : signup
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSubmit]
  );

  const handleClick = () => {
    navigate("/forgot-password", {
      state: "user",
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleFormSubmit}>
      {currentView === "signup" && (
        <Fragment>
          <div className="grid md:grid-cols-2 gap-2">
            <div className="relative">
              <Input
                label={"First name"}
                inputPlaceholder={"Enter First name"}
                labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
                inputClasses={
                  "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose !text-normal"
                }
                inputType={"text"}
                inputName="firstName"
                value={values?.firstName || ""}
                onChange={handleChange}
                {...formValidate(errors, "firstName")}
              />
            </div>
            <div className="relative">
              <Input
                label={"Last name"}
                inputPlaceholder={"Enter Last name"}
                labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
                inputClasses={
                  "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose !text-normal"
                }
                inputType={"text"}
                inputName="lastName"
                value={values?.lastName || ""}
                onChange={handleChange}
                {...formValidate(errors, "lastName")}
              />
            </div>
          </div>
          <div className="relative">
            <Input
              label={"Email Address"}
              inputPlaceholder={"Enter Email Address"}
              labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
              inputClasses={
                "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose !text-normal"
              }
              inputType={"email"}
              inputName="email"
              value={values?.email || ""}
              onChange={handleChange}
              {...formValidate(errors, "email")}
            />
          </div>
          <div className="relative">
            <Input
              inputType={"password"}
              label={"Password"}
              inputPlaceholder={"Enter Password"}
              labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
              inputClasses={
                "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose !text-normal"
              }
              iconClasses={"!text-white"}
              inputName="password"
              value={values?.password || ""}
              onChange={handleChange}
              {...formValidate(errors, "password")}
            />
          </div>
          <div className="relative">
            <Input
              inputType={"password"}
              label={"Confirm Password"}
              inputPlaceholder={"Re-Enter Password"}
              labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
              inputName="confirmPassword"
              inputClasses={
                "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose !text-normal"
              }
              iconClasses={"!text-white"}
              value={values?.confirmPassword || ""}
              onChange={handleChange}
              {...formValidate(errors, "confirmPassword")}
            />
          </div>
        </Fragment>
      )}
      {currentView === "signin" && (
        <Fragment>
          <div className="relative">
            <Input
              label={"Email Address"}
              inputPlaceholder={"Enter Email Address"}
              labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
              inputClasses={
                "!h-12 !rounded-3xl !border-shades-05/50 !text-slate-700 placeholder:!text-slate-500 focus:!border-chestnut-rose"
              }
              inputType={"email"}
              inputName="loginEmail"
              value={values?.loginEmail || ""}
              onChange={handleChange}
              {...formValidate(errors, "loginEmail")}
            />
          </div>
          {loginAs === "user" && (
            <div className="relative">
              <Input
                inputType={"password"}
                label={"Password"}
                inputPlaceholder={"Enter Password"}
                labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
                inputClasses={
                  "!h-12 !rounded-3xl !border-shades-o5/50 !text-slate-700 placeholder:!text-slate-500 focus:!border-chestnut-rose"
                }
                iconClasses={"!text-white"}
                inputName="loginPassword"
                value={values?.loginPassword || ""}
                onChange={handleChange}
                {...formValidate(errors, "loginPassword")}
              />
            </div>
          )}
          {loginAs === "donor" && donorData?.password === 1 && (
            <div className="relative">
              <Input
                inputType={"password"}
                label={"Password"}
                inputPlaceholder={"Enter Password"}
                labelClasses={"!text-slate-600 !text-xs !mb-1 !ml-3"}
                inputClasses={
                  "!h-12 !rounded-3xl !border-shades-05/50 !text-black placeholder:!text-slate-500 focus:!border-chestnut-rose"
                }
                iconClasses={"!text-white"}
                inputName="loginPassword"
                value={values?.loginPassword}
                onChange={handleChange}
                {...formValidate(errors, "loginPassword")}
              />
            </div>
          )}
        </Fragment>
      )}
      {currentView === "signin" && (
        <div className="flex items-end sm:items-center justify-between !mt-1 !mx-2">
          <div className="relative flex justify-end">
            <Button
              buttonLabel={`Login as ${loginAs === "user" ? "Donor" : "User"}?`}
              buttonLabelClasses={"!text-sm !text-slate-700"}
              buttonClasses={"!h-auto !py-1 !px-0 !bg-transparent"}
              buttonFunction={() => {
                loginAs === "user"
                  ? navigate("/authentication/donor")
                  : navigate("/authentication");
                setLoginAs((pre) => (pre === "user" ? "donor" : "user"));
              }}
            />
          </div>
          <div className="relative flex justify-end">
            <Button
              buttonLabel={"Forget Password?"}
              buttonLabelClasses={"!text-sm !text-slate-700"}
              buttonClasses={"!h-auto !py-1 !px-0 !bg-transparent"}
              buttonFunction={handleClick}
            />
          </div>
        </div>
      )}
      <div className="relative">
        <Button
          buttonLabel={
            currentView === "signin"
              ? "Sign In"
              : currentView === "signup"
              ? "Sign Up"
              : ""
          }
          buttonType="submit"
          buttonLabelClasses={"!text-lg !font-semibold"}
          buttonClasses={"!w-full !h-12 !rounded-3xl"}
          loading={currentView === "signup" ? signUpPending : loginPending}
          isDisable={currentView === "signup" ? signUpPending : loginPending}
          buttonIconPosition="left"
        />
      </div>
      {loginData?.status && loginData?.status !== 200 && (
        <div className="text-center text-chestnut-rose">
          {loginData?.message}
        </div>
      )}
      {loginAs === "donor" && loginData?.status === 200 && (
        <div className="text-center text-chestnut-rose">
          {loginData?.message}
        </div>
      )}
    </form>
  );
};

export default AuthenticationForm;
